import { POSTHOG_HOST, POSTHOG_KEY } from '@/env'
import { isMockHarness } from '@/utils/mockHarness'
import posthog from 'posthog-js'

let initialized = false

// IV-31 — the Tremendous redemption URL is permanent, unrevocable bearer authority. Autocapture
// records clicked-element attributes (attr__href), so every outgoing property tree is redacted in
// place. Defence in depth only: the primary control is that the URL never enters the DOM. This does
// NOT reach Google Tag Manager, which is configured outside this repository.
// The scrubber body below is kept TEXTUALLY IDENTICAL to its inlined copy in vite.config.ts (modulo
// TS annotations): both the HTML-snippet and npm-module PostHog instances capture. Change both.
const REDEMPTION_URL_RE = /https?:\/\/(?:www\.)?tremendous\.com\/rewards\/payout\/[^\s"'<>]*/gi
const REDEMPTION_URL_REDACTED = '[redacted]'
export const scrubRedemptionUrls = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') return value.replace(REDEMPTION_URL_RE, REDEMPTION_URL_REDACTED)
  if (depth >= 12 || value === null || typeof value !== 'object') return value
  for (const [key, item] of Object.entries(value)) {
    const scrubbed = scrubRedemptionUrls(item, depth + 1)
    if (scrubbed !== item) (value as Record<string, unknown>)[key] = scrubbed
  }
  return value
}

export const initPostHog = (): void => {
  // Dev mock harness: its fake wallet events must not reach a real project. `initialized` stays false,
  // so isPostHogReady() keeps track() away from the uninitialised instance too.
  if (isMockHarness()) return
  if (initialized || typeof window === 'undefined' || !POSTHOG_KEY) return
  initialized = true
  // When the HTML snippet has already called posthog.init(), the library marks
  // itself as loaded. Calling init() again would create a duplicate instance, so
  // we skip it and rely on the snippet-initialized instance.
  if (posthog.__loaded) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    person_profiles: 'identified_only',
    debug: false,
    before_send: (event) => {
      if (!event || !event.properties) return event
      event.properties = scrubRedemptionUrls(event.properties, 0) as typeof event.properties
      return event
    }
  })
}

export const isPostHogReady = (): boolean => initialized || !!posthog.__loaded

export { posthog }
